/**
 * 安全工具函数
 *
 * 用于防止XSS、注入等安全攻击
 */

/**
 * 转义HTML特殊字符，防止XSS攻击
 */
export function escapeHtml(unsafe: string): string {
  if (typeof unsafe !== 'string') {
    return '';
  }

  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * 验证并清理URL，防止javascript:等伪协议
 */
export function sanitizeURL(url: string): string {
  if (typeof url !== 'string') {
    return '';
  }

  try {
    const parsed = new URL(url);

    // 只允许 http/https 协议
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      console.warn('不安全的URL协议:', parsed.protocol);
      return '';
    }

    return url;
  } catch (error) {
    console.warn('URL解析失败:', url);
    return '';
  }
}

/**
 * 安全的JSON解析，防止原型污染和代码注入
 */
export function safeJsonParse<T>(json: string | null, fallback: T): T {
  if (!json || typeof json !== 'string') {
    return fallback;
  }

  try {
    const parsed = JSON.parse(json);

    // 防止原型污染
    if (parsed && typeof parsed === 'object') {
      const dangerousKeys = ['__proto__', 'constructor', 'prototype'];

      const checkObject = (obj: any): boolean => {
        if (!obj || typeof obj !== 'object') return true;

        for (const key of Object.keys(obj)) {
          // 检查危险键
          if (dangerousKeys.includes(key)) {
            console.warn('检测到危险键:', key);
            return false;
          }

          // 递归检查嵌套对象
          if (!checkObject(obj[key])) {
            return false;
          }
        }

        return true;
      };

      if (!checkObject(parsed)) {
        console.warn('JSON包含危险内容，使用fallback');
        return fallback;
      }
    }

    return parsed;
  } catch (error) {
    console.error('JSON解析失败:', error);
    return fallback;
  }
}

/**
 * 验证并清理缓存键
 * 只允许字母、数字、下划线和连字符，限制长度
 */
export function sanitizeCacheKey(keyword: string): string {
  if (typeof keyword !== 'string') {
    return '';
  }

  return keyword
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\-_]/g, '_') // 替换非法字符为下划线
    .substring(0, 50); // 限制长度为50
}

/**
 * 验证查询字符串
 * 防止注入攻击
 */
export function validateQuery(query: string): { valid: boolean; sanitized?: string; error?: string } {
  if (typeof query !== 'string') {
    return { valid: false, error: 'Query must be a string' };
  }

  const trimmed = query.trim();

  // 长度限制
  if (trimmed.length === 0) {
    return { valid: false, error: 'Query cannot be empty' };
  }

  if (trimmed.length > 200) {
    return { valid: false, error: 'Query too long (max 200 characters)' };
  }

  // 只允许安全字符（字母、数字、空格、中文字符和常见符号）
  const allowed = /^[a-zA-Z0-9\s\u4e00-\u9fa5\-_.?!@#$%^&*()+=\[\]{}|\\:"'<>,.?\/~`]+$/;

  if (!allowed.test(trimmed)) {
    return { valid: false, error: 'Query contains invalid characters' };
  }

  // 检查潜在的注入模式
  const dangerousPatterns = [
    /<script[^>]*>/i,
    /javascript:/i,
    /on\w+\s*=/i, // 事件处理器如 onclick=
    /<iframe/i,
    /<embed/i,
    /<object/i,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(trimmed)) {
      return { valid: false, error: 'Query contains dangerous patterns' };
    }
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * 清理用户输入，防止prompt注入
 * 移除常见的注入模式
 */
export function sanitizePrompt(prompt: string): string {
  if (typeof prompt !== 'string') {
    return '';
  }

  const dangerousPatterns = [
    /ignore\s+(all\s+)?(previous|above|earlier)\s+instructions?/gi,
    /disregard\s+(everything|all)/gi,
    /system:\s*/gi,
    /assistant:\s*/gi,
    /<script[^>]*>.*?<\/script>/gis,
    /javascript:\s*/gi,
  ];

  let sanitized = prompt;

  dangerousPatterns.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '');
  });

  return sanitized.trim();
}

/**
 * 验证IP地址格式
 */
export function isValidIP(ip: string): boolean {
  if (typeof ip !== 'string') {
    return false;
  }

  // IPv4正则
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  // IPv6正则（简化）
  const ipv6Regex = /^([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}$/i;

  if (ipv4Regex.test(ip)) {
    // 检查每个段是否在0-255之间
    const parts = ip.split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  }

  return ipv6Regex.test(ip);
}

/**
 * 生成安全的错误消息（不泄露敏感信息）
 */
export function createSafeError(userMessage: string, logError?: any): {
  error: string;
  timestamp: number;
  logged: boolean;
} {
  // 记录详细错误到日志（但不在响应中返回）
  if (logError) {
    console.error('[Security Error Details]:', logError);
  }

  return {
    error: userMessage,
    timestamp: Date.now(),
    logged: !!logError
  };
}
